import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { BasePageView } from "by/gamefactory/cityversetycoon/scenes/common/components/BasePageView";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { IBuildingModel } from "by/gamefactory/cityversetycoon/scenes/common/models/Building/IBuildingModel";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { IDestroyOptions, Sprite, Text, TextStyle } from "pixi.js";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { UnboardingBuildingContainer } from "../../../components/unboarding/UnboardingBuildingContainer";
import { UnboardingHeaderContainer } from "../../../components/unboarding/UnboardingHeaderContainer";
import { UnboardingType } from "../../../components/unboarding/UnboardingType";
import { UnboardingResourcePackage } from "../../../resources/UnboardingResourcePackage";

export class FirstPage extends BasePageView {
    private static readonly BUTTON_TEXT = "NEXT";
    private static readonly MAX_BUILDINGS_COUNT = 3;

    private readonly operationControls: PromiseCompletionSource<void>;
    private readonly commonResources: CommonResourcesPackage;
    private readonly unboardingResources: UnboardingResourcePackage;
    private readonly myBuildingsDynamicResources: DynamicResourcesPackage;
    private readonly playerModel: IPlayerModel;
    private readonly type: UnboardingType;

    private headerTextContainer: UnboardingHeaderContainer;
    private background: Sprite;
    private button: ButtonWithScaleAnimation;
    private buildingLeftLight: Sprite;
    private buildingRightLight: Sprite;
    private buildings: Array<UnboardingBuildingContainer>;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        unboardingResources: UnboardingResourcePackage,
        myBuildingsDynamicResources: DynamicResourcesPackage,
        canvasScaler: ICanvasScaler,
        playerModel: IPlayerModel,
        type: UnboardingType
    ) {
        super(canvasScaler);
        this.operationControls = operationControls;
        this.commonResources = commonResources;
        this.unboardingResources = unboardingResources;
        this.myBuildingsDynamicResources = myBuildingsDynamicResources;
        this.playerModel = playerModel;
        this.type = type;
        this.buildings = [];
        this.build();
    }

    protected override build(): void {
        this.buildings = this.playerModel.buildings.value.map((value) => this.buildingContainerFactory(value));
        this.buildings.slice(0, Math.min(this.buildings.length, FirstPage.MAX_BUILDINGS_COUNT));
        this.addChild(...this.buildings)
        this.headerTextContainer = new UnboardingHeaderContainer(this.unboardingResources, this.type, this.buildings.length > 1);
        this.addChild(this.headerTextContainer);

        this.buildingLeftLight = new Sprite(this.unboardingResources.assets_views_unboarding_side_light_png);
        this.buildingLeftLight.width = 276;
        this.buildingLeftLight.height = 680;
        this.addChild(this.buildingLeftLight);

        this.buildingRightLight = new Sprite(this.unboardingResources.assets_views_unboarding_side_light_png);
        this.buildingRightLight.width = 276;
        this.buildingRightLight.height = 680;
        this.buildingRightLight.pivot.x = this.buildingRightLight.width;
        this.buildingRightLight.scale.x *= -1;
        this.addChild(this.buildingRightLight);

        this.background = new Sprite(this.unboardingResources.assets_views_unboarding_stars_bg_first_page_png);
        this.addChild(this.background);

        this.button = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_button_green_png);
        this.button.width = 297;
        this.button.height = 156;
        this.button.onUp.add(this.buttonClickHandle, this);
        this.addChild(this.button);

        const buttonText = new Text(FirstPage.BUTTON_TEXT, new ButtonTextStyle());
        buttonText.position.set(105, 39);
        this.button.defaultView.addChild(buttonText);
        super.build();
    }

    private buttonClickHandle() {
        this.button.interactive = false;
        this.operationControls.complete();
    }

    protected override layout(width: number, _height: number): void {
        this.headerTextContainer.position.set(212, 260);
        this.buildingLeftLight.position.set(-166, 737);
        this.buildingRightLight.position.set(757.5, 737);
        this.button.position.set(264, 1550);
        switch (this.type) {
            case UnboardingType.OWNER:
                this.layoutOwnerBuildings();
                break;
            case UnboardingType.RENTER:
                this.buildings[0].position.set(0, 610);
                break;
        }
    }

    private buildingContainerFactory(buildingModel: IBuildingModel): UnboardingBuildingContainer {
        const building = new UnboardingBuildingContainer(
            this.unboardingResources,
            this.commonResources,
            this.myBuildingsDynamicResources,
            buildingModel
        );
        return building;
    }

    private layoutOwnerBuildings() {
        const positions = [
            { x: 56, y: 661, angle: 0, scale: 0.88 },
            { x: -225, y: 1050, angle: -30, scale: 0.55 },
            { x: 628, y: 733, angle: 16, scale: 0.65 }
        ];

        this.buildings.reverse().forEach((building, index) => {
            const posIndex = Math.min(index, positions.length - 1);
            building.position.set(positions[posIndex].x, positions[posIndex].y);
            building.scale.set(positions[posIndex].scale);
            building.angle = positions[posIndex].angle;
        });

        this.buildingLeftLight.position.x = -123;
        this.buildingRightLight.position.x = 728;
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.button.onUp.remove(this.buttonClickHandle, this);
        super.destroy(options);
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            dropShadow: true,
            dropShadowAlpha: 0.5,
            dropShadowAngle: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 4,
            fontSize: 85,
            leading: 15,
            letterSpacing: 5.25
        });
    }
}